import { LeaderboardEntry, PersonalizedLeaderboardEntry } from '../types';
import { 
  arrayToCSV, 
  downloadCSV, 
  downloadPDF, 
  createPDF, 
  addTableToPDF,
  formatTimeForDisplay,
  formatScoreForDisplay,
  generateFilename,
  getDataModeLabel,
  getPeriodLabel
} from './exportUtils';
import { formatTimeMinutes } from './personalizedDataService';
import { formatSpendDisplay } from './cursorSpendService';

/**
 * Export service for Cursor Leaderboard (LeaderboardTable component)
 */

export interface LeaderboardExportOptions {
  dataMode: string;
  reportingPeriod: string;
  selectedWeek: string;
  selectedMetric: string;
  isPersonalized: boolean;
}

/**
 * Prepare data for CSV export
 */
const prepareLeaderboardDataForCSV = (
  users: (LeaderboardEntry | PersonalizedLeaderboardEntry)[],
  isPersonalized: boolean
): any[] => {
  return users.map((user, index) => {
    const baseData = {
      Rank: index + 1,
      ...(isPersonalized && 'teamMemberName' in user 
        ? { 'Team Member': user.teamMemberName }
        : { Email: user.email }
      ),
      ...(isPersonalized && 'efficiencyScore' in user && {
        'Efficiency Score (Lines/Hour)': formatScoreForDisplay(user.efficiencyScore || 0, 2),
        'Cursor Time': formatTimeMinutes(user.cursorTimeMinutes || 0)
      }),
      'Spend (Cycle to Date)':
        user.spendPerUser != null
          ? formatSpendDisplay(user.spendPerUser, user.spendOnDemandPerUser)
          : '',
      'Chat Total Applies': user.chatTotalApplies,
      'Chat Total Accepts': user.chatTotalAccepts,
      'Chat Accepted Lines Added': user.chatAcceptedLinesAdded,
      'Total Lines Added': user.totalLinesAdded,
      'Chat Tabs Shown': user.chatTabsShown,
      'Tabs Accepted': user.tabsAccepted,
      'Edit Requests': user.editRequests,
      'Ask Requests': user.askRequests,
      'Agent Requests': user.agentRequests,
      'Cmd+K Requests': user.cmdkRequests
    };

    return baseData;
  });
};

/**
 * Export Cursor Leaderboard data to CSV
 */
export const exportLeaderboardToCSV = (
  users: (LeaderboardEntry | PersonalizedLeaderboardEntry)[],
  options: LeaderboardExportOptions
): void => {
  if (users.length === 0) {
    alert('No data available to export');
    return;
  }

  try {
    const data = prepareLeaderboardDataForCSV(users, options.isPersonalized);
    const csvContent = arrayToCSV(data);
    
    const dataMode = getDataModeLabel(options.dataMode);
    const period = getPeriodLabel(options.reportingPeriod);
    const boardType = options.isPersonalized ? 'Enhanced_Leaderboard' : 'Basic_Leaderboard';
    
    const filename = generateFilename(
      `cursor_leaderboard_${boardType}_${dataMode}_${period}_${options.selectedWeek}`,
      'csv'
    );
    
    downloadCSV(csvContent, filename);
    console.log('✅ [EXPORT] Cursor Leaderboard CSV exported:', filename);
  } catch (error) {
    console.error('❌ [EXPORT] Failed to export Cursor Leaderboard CSV:', error);
    alert('Failed to export CSV. Please try again.');
  }
};

/**
 * Export Cursor Leaderboard data to PDF
 */
export const exportLeaderboardToPDF = (
  users: (LeaderboardEntry | PersonalizedLeaderboardEntry)[],
  options: LeaderboardExportOptions
): void => {
  if (users.length === 0) {
    alert('No data available to export');
    return;
  }

  try {
    const dataMode = getDataModeLabel(options.dataMode);
    const period = getPeriodLabel(options.reportingPeriod);
    const boardType = options.isPersonalized ? 'Enhanced AI Leaderboard' : 'AI Users Leaderboard';
    
    const title = `${boardType} - ${dataMode} (${period})`;
    const pdf = createPDF(title, 'landscape');

    // Add metadata
    pdf.setFontSize(12);
    pdf.text(`Week: ${options.selectedWeek}`, 20, 40);
    pdf.text(`Sorted by: ${options.selectedMetric}`, 20, 50);
    pdf.text(`Total ${options.isPersonalized ? 'Team Members' : 'Users'}: ${users.length}`, 20, 60);

    // Prepare table headers
    const headers = ['Rank'];
    if (options.isPersonalized) {
      headers.push('Team Member');
      headers.push('Efficiency (Lines/Hour)');
      headers.push('Cursor Time');
    } else {
      headers.push('Email');
    }
    headers.push(
      'Spend (Cycle to Date)',
      'Chat Applies',
      'Chat Accepts', 
      'Accepted Lines',
      'Total Lines',
      'Tabs Shown',
      'Tabs Accepted',
      'Edit Requests',
      'Ask Requests',
      'Agent Requests',
      'Cmd+K Requests'
    );

    // Prepare table data
    const tableData = users.map((user, index) => {
      const row = [String(index + 1)];
      
            if (options.isPersonalized && 'teamMemberName' in user) {
        row.push(user.teamMemberName || '');
        row.push(formatScoreForDisplay(user.efficiencyScore || 0, 2));       
        row.push(formatTimeMinutes(user.cursorTimeMinutes || 0));
      } else {
        row.push(user.email);
      }
      
      row.push(
        user.spendPerUser != null
          ? formatSpendDisplay(user.spendPerUser, user.spendOnDemandPerUser)
          : '-',
        String(user.chatTotalApplies),
        String(user.chatTotalAccepts),
        String(user.chatAcceptedLinesAdded),
        String(user.totalLinesAdded),
        String(user.chatTabsShown),
        String(user.tabsAccepted),
        String(user.editRequests),
        String(user.askRequests),
        String(user.agentRequests),
        String(user.cmdkRequests)
      );
      
      return row;
    });

    // Add table to PDF
    addTableToPDF(pdf, headers, tableData, 70, {
      styles: { fontSize: 6 },
      columnStyles: {
        0: { cellWidth: 10 }, // Rank
        1: { cellWidth: options.isPersonalized ? 25 : 40 } // Team Member/Email
      }
    });

    const filename = generateFilename(
      `cursor_leaderboard_${boardType.replace(/\s+/g, '_')}_${dataMode}_${period}_${options.selectedWeek}`,
      'pdf'
    );
    
    downloadPDF(pdf, filename);
    console.log('✅ [EXPORT] Cursor Leaderboard PDF exported:', filename);
  } catch (error) {
    console.error('❌ [EXPORT] Failed to export Cursor Leaderboard PDF:', error);
    alert('Failed to export PDF. Please try again.');
  }
};
